import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import "./PaymentPlan.css";

import { useApiQuery, resolveFileUrl } from "../api/client";

const steps = [
  {
    icon: <ReceiptLongRoundedIcon />,
    title: "Booking",
    desc: "Reserve your unit with a simple booking amount.",
  },
  {
    icon: <PaymentsRoundedIcon />,
    title: "Installments",
    desc: "Monthly/quarterly installments designed for real life.",
  },
  {
    icon: <EventAvailableRoundedIcon />,
    title: "Possession",
    desc: "Final payment and keys — smooth handover process.",
  },
];

const PaymentPlan = () => {
  const { ref, inView } = useInView({ threshold: 0.18, triggerOnce: true });

  const { data: plansData, isLoading, isError } = useApiQuery("/payment-plans");
  const plans = plansData || [];
  const { data: scheduleDoc } = useApiQuery("/documents/paymentSchedule");

  const openSchedule = () => {
    if (scheduleDoc?.fileUrl) {
      window.open(resolveFileUrl(scheduleDoc.fileUrl), "_blank", "noopener,noreferrer");
    }
  };

  return (
    <section className="pay" id="payment" ref={ref}>
      <div className="pay__container">
        <motion.div
          className="pay__head"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <div className="pay__kicker">PAYMENT PLAN</div>
          <h2 className="pay__title">Clear installments. No confusion.</h2>
          <p className="pay__sub">
            Choose a plan that matches your timeline. We’ll share a full
            schedule and floor plan on request.
          </p>
        </motion.div>

        {/* Timeline / steps */}
        <motion.div
          className="pay__steps"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.06 }}
        >
          {steps.map((s, idx) => (
            <div className="pay__step" key={idx}>
              <div className="pay__stepIcon">{s.icon}</div>
              <div className="pay__stepText">
                <div className="pay__stepTitle">{s.title}</div>
                <div className="pay__stepDesc">{s.desc}</div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Plans */}
        <motion.div
          className="pay__grid"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.12 }}
        >
          {isLoading && <div className="pay__state">Loading payment plans…</div>}
          {isError && <div className="pay__state">Couldn't load payment plans right now.</div>}
          {!isLoading && !isError && plans.length === 0 && (
            <div className="pay__state">Payment plans coming soon.</div>
          )}

          {plans.map((p) => (
            <div className="pay__card" key={p._id}>
              {p.badge && <div className="pay__badge">{p.badge}</div>}
              <h3 className="pay__cardTitle">{p.name}</h3>

              <ul className="pay__list">
                {p.points.map((pt, i) => (
                  <li key={i}>{pt}</li>
                ))}
              </ul>

              <button
                onClick={openSchedule}
                className="pay__btn"
                type="button"
              >
                Get Full Schedule <ArrowOutwardRoundedIcon />
              </button>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default PaymentPlan;
